import { useCallback, useMemo, useRef, useState } from 'react';

import {
  ATTACHMENT_FIELD_DESCRIPTORS,
  FILE_MIME_TYPES,
  FormElementKind,
  getAttachmentFieldAccept,
  getAttachmentFieldDescriptor,
  PdfAttachmentFormElementAnswerSchema,
  PdfAttachmentFormElement as PdfAttachmentFormElementType,
  type AttachmentFieldMimeType,
  type PdfAttachmentFile,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { z } from 'zod';

import AttachmentIcon from '@/components/icons/attachment';
import DeleteIcon from '@/components/icons/delete';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { Dropzone, DropzoneContent, useDropzoneValue } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toaster';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useIsAWA } from '@/utils/awa';
import { getFileTypeIcon } from '@/utils/file-type';
import { postToPresignedTarget } from '@/utils/upload';

import { ConfigLabel } from '../config-label';
import { FieldShell } from '../field-shell';

const { maxFiles: PDF_ATTACHMENT_MAX_FILES, maxFileSizeBytes: PDF_ATTACHMENT_MAX_FILE_SIZE_BYTES } = getAttachmentFieldDescriptor(
  FormElementKind.PdfAttachment,
);
const PDF_ATTACHMENT_DROPZONE_ACCEPT = getAttachmentFieldAccept(FormElementKind.PdfAttachment);
const PDF_ATTACHMENT_INPUT_ACCEPT = Object.keys(PDF_ATTACHMENT_DROPZONE_ACCEPT).join(',');
// Indexed off the const, not getAttachmentFieldDescriptor(), which widens the tuple to FileMimeType[].
const PDF_ATTACHMENT_ACCEPTED_MIME_TYPES = ATTACHMENT_FIELD_DESCRIPTORS[FormElementKind.PdfAttachment].acceptedMimeTypes;
const PDF_ATTACHMENT_ACCEPTED_LABEL = PDF_ATTACHMENT_ACCEPTED_MIME_TYPES.map((mimeType) => FILE_MIME_TYPES[mimeType].toUpperCase()).join(', ');
const PDF_ATTACHMENT_MAX_SIZE_LABEL = `${Math.round(PDF_ATTACHMENT_MAX_FILE_SIZE_BYTES / (1024 * 1024))}MB`;

type PdfAttachmentMimeType = AttachmentFieldMimeType<FormElementKind.PdfAttachment>;

function isAcceptedMimeType(mimeType: string): mimeType is PdfAttachmentMimeType {
  return (PDF_ATTACHMENT_ACCEPTED_MIME_TYPES as readonly string[]).includes(mimeType);
}

function DropOverlay() {
  const { isDragActive, isDragReject } = useDropzoneValue();
  if (!isDragActive) return null;
  return (
    <div className="border-grey-4 bg-grey-1/80 absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed">
      <span className="text-secondary-text text-sm">{isDragReject ? `Only ${PDF_ATTACHMENT_ACCEPTED_LABEL} files are allowed` : 'Drop PDFs here'}</span>
    </div>
  );
}

type PdfAttachmentValue = z.infer<typeof PdfAttachmentFormElementAnswerSchema>['value'];
type PdfFile = PdfAttachmentFile;

interface UploadingFile {
  id: string;
  name: string;
  error?: string;
}

export const PdfAttachmentFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  isPreview = false,
  hideLabel,
  error,
}: {
  element: PdfAttachmentFormElementType;
  value?: PdfAttachmentValue;
  onChange: (value: PdfAttachmentValue) => void;
  onBlur?: () => void;
  readonly?: boolean;
  isPreview?: boolean;
  hideLabel?: boolean;
  error?: string;
}) => {
  const { isAWA } = useIsAWA();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [fileSizeError, setFileSizeError] = useState(false);
  const files: PdfFile[] = useMemo(() => value ?? [], [value]);
  const currentCount = files.length;
  const isLimitReached = currentCount >= PDF_ATTACHMENT_MAX_FILES;
  const uploadingCount = uploadingFiles.length;

  const uploadFile = useCallback(
    async (file: File, contentType: PdfAttachmentMimeType): Promise<PdfFile> => {
      const uploadId = crypto.randomUUID();

      setUploadingFiles((prev) => [...prev, { id: uploadId, name: file.name }]);

      try {
        const dto = { filename: `${Date.now()}-${file.name}`, contentType };
        const response = isAWA
          ? await AWAApiV2Service.GeneratePdfAttachmentFormFieldPresignedUploadUrl(dto)
          : await ApiService.GeneratePdfAttachmentFormFieldPresignedUploadUrl(dto);
        const { upload, fileUrl } = await response.json();

        await postToPresignedTarget(upload, file);

        return { url: fileUrl, name: file.name };
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadId));
      }
    },
    [isAWA],
  );

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      setFileSizeError(false);
      const filesToProcess = Array.from(fileList);
      const remainingSlots = PDF_ATTACHMENT_MAX_FILES - currentCount;
      if (remainingSlots <= 0) return;

      const validFiles: { file: File; contentType: PdfAttachmentMimeType }[] = [];
      for (const file of filesToProcess.slice(0, remainingSlots)) {
        if (!isAcceptedMimeType(file.type)) {
          setUploadingFiles((prev) => [
            ...prev,
            { id: crypto.randomUUID(), name: file.name, error: `Only ${PDF_ATTACHMENT_ACCEPTED_LABEL} files are allowed` },
          ]);
          continue;
        }
        if (file.size > PDF_ATTACHMENT_MAX_FILE_SIZE_BYTES) {
          setFileSizeError(true);
          continue;
        }
        validFiles.push({ file, contentType: file.type });
      }

      const uploaded: PdfFile[] = [];
      for (const { file, contentType } of validFiles) {
        try {
          const result = await uploadFile(file, contentType);
          uploaded.push(result);
        } catch {
          setUploadingFiles((prev) => [...prev, { id: crypto.randomUUID(), name: file.name, error: 'Upload failed' }]);
        }
      }

      if (uploaded.length > 0) {
        onChange([...files, ...uploaded]);
        onBlur?.();
      }
    },
    [currentCount, files, onChange, onBlur, uploadFile],
  );

  const handleRemove = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    onChange(updated);
  };

  if (isPreview) {
    return (
      <FieldShell label={element.label} required={element.required} hideLabel={hideLabel}>
        <div>
          <Button type="button" icon={<AttachmentIcon size={20} />} disabled>
            Add PDF file
          </Button>
        </div>
      </FieldShell>
    );
  }

  if (readonly) {
    return (
      <FieldShell label={element.label} required={element.required} hideLabel={hideLabel}>
        {files.length > 0 ? (
          <ul>
            {files.map((file, index) => (
              <li key={index} className="border-grey-2 flex items-center gap-2 border-b px-1 py-2">
                <span className="flex-shrink-0">{getFileTypeIcon(file.mediaType ?? PDF_ATTACHMENT_ACCEPTED_MIME_TYPES[0])}</span>
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-sm hover:underline">
                  {file.name}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-secondary-text text-sm">No attachments</p>
        )}
      </FieldShell>
    );
  }

  return (
    <FieldShell label={element.label} required={element.required} hideLabel={hideLabel} error={error}>
      <Dropzone
        accept={PDF_ATTACHMENT_DROPZONE_ACCEPT}
        multiple
        noClick
        noDragEventsBubbling
        maxSize={PDF_ATTACHMENT_MAX_FILE_SIZE_BYTES}
        maxFiles={PDF_ATTACHMENT_MAX_FILES - currentCount}
        disabled={isLimitReached || uploadingCount > 0}
        onDropAccepted={(acceptedFiles) => handleFiles(acceptedFiles)}
        onDropRejected={(fileRejections) => {
          const rejection = fileRejections[0];
          if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
            toast.error('Only PDF files are supported.');
          } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
            setFileSizeError(true);
          } else if (rejection.errors.some((e) => e.code === 'too-many-files')) {
            toast.error(`Maximum of ${PDF_ATTACHMENT_MAX_FILES} files allowed.`);
          } else {
            toast.error('File is invalid.');
          }
        }}>
        <DropzoneContent className="mx-0 items-start border-none !bg-transparent p-0">
          <DropOverlay />
          {files.length > 0 && (
            <ul className="w-full">
              {files.map((file, index) => (
                <li key={index} className="border-grey-2 flex items-center gap-2 border-b px-1 py-2">
                  <span className="flex-shrink-0">{getFileTypeIcon(file.mediaType ?? PDF_ATTACHMENT_ACCEPTED_MIME_TYPES[0])}</span>
                  <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-sm hover:underline">
                    {file.name}
                  </a>
                  <button type="button" className="text-default-red flex items-center gap-1 text-sm" onClick={() => handleRemove(index)}>
                    <DeleteIcon size={16} />
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}

          {uploadingFiles.map((file) => (
            <div key={file.id} className="border-grey-2 flex w-full items-center gap-1 border-b px-1 py-2">
              <span className="flex-shrink-0">{getFileTypeIcon('application/pdf')}</span>
              <span className="flex-1 truncate text-sm">{file.name}</span>
              {file.error ? (
                <span className="flex items-center gap-2">
                  <span className="text-default-red text-xs">{file.error}</span>
                  <button
                    type="button"
                    className="text-default-red flex items-center gap-1 text-sm"
                    onClick={() => setUploadingFiles((prev) => prev.filter((f) => f.id !== file.id))}>
                    <DeleteIcon size={16} />
                    Remove
                  </button>
                </span>
              ) : (
                <span className="text-secondary-text text-xs">Uploading…</span>
              )}
            </div>
          ))}

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={PDF_ATTACHMENT_INPUT_ACCEPT}
              multiple
              className="hidden"
              onChange={async (e) => {
                if (e.target.files?.length) {
                  await handleFiles(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <Button
              type="button"
              icon={<AttachmentIcon size={20} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isLimitReached || uploadingCount > 0}>
              Add PDF file
            </Button>
            {currentCount > 0 && (
              <span className="text-muted-foreground ml-2 text-sm">
                {currentCount}/{PDF_ATTACHMENT_MAX_FILES} PDF files
              </span>
            )}
            {isLimitReached && <p className="text-muted-foreground mt-1 text-sm">Maximum number of PDFs reached ({PDF_ATTACHMENT_MAX_FILES})</p>}
            {fileSizeError && (
              <div className="text-default-red mt-2 flex items-center gap-1 text-sm">
                <WarningTriangleIcon size={16} className="flex-shrink-0" />
                <span>File must be under {PDF_ATTACHMENT_MAX_SIZE_LABEL}.</span>
              </div>
            )}
          </div>
        </DropzoneContent>
      </Dropzone>
    </FieldShell>
  );
};

export function PdfAttachmentFormElementConfig({
  element,
  onChange,
}: {
  element: { label: string; required: boolean };
  onChange?: (updates: Partial<{ label: string; required: boolean }>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      {/* Required option hidden until SMM supports this field — enabling it would cause processAnswers to fail */}
    </div>
  );
}
